// ─── hierarchyTreeUtils.js ────────────────────────────────────────────────────
// Shared helpers for the hierarchy-tree API used by HospitalsTable,
// DepartmentTable, and PhysicianTable.
//
// API endpoint:
//   GET /client/dashboard/hierarchy-tree/?entity_type=<type>&entity_id=<id>
//
// entity_type values:
//   HospitalsTable   → "health_system"
//   DepartmentTable  → "hospital"
//   PhysicianTable   → "department"

// ─── Transform API node → flat row shape expected by CQTreeTable ──────────────
// The API nests metrics under row.metrics.* — we hoist them to the top level
// so column accessorKey works directly (e.g. accessorKey: 'chart_accuracy').
// children → subRows so CQTreeTable can find them via getSubRows.
export function transformNode(node, parentIds = {}) {
  if (!node) return null;
  const { children, metrics, ...rest } = node;
  return {
    ...rest,
    // hoist all metrics fields to top level
    ...parentIds, // injects hospital_id, department_id onto every child
    ...(metrics ?? {}),
    // recurse into children, renamed to subRows
    subRows: children?.map(child => transformNode(child, {
      health_system_id: node.type === 'health_system' ? node.id : parentIds.health_system_id,
      hospital_id:    node.type === 'hospital'   ? node.id : parentIds.hospital_id,
      department_id:  node.type === 'department' ? node.id : parentIds.department_id,
    }))
  };
}

// Transform the root response.
// The API returns a single root object (the health_system / hospital / department).
// We want its children as the top-level rows of the table.
export function transformHierarchyResponse(response) {
  if (!response) return [];
  const root = Array.isArray(response) ? response[0] : response;
  if (!root?.children?.length) return [];
  // Seed parent IDs from the root node so children always have full context
  const seedIds = {
    health_system_id: root.type === 'health_system' ? root.id : undefined,
    hospital_id:      root.type === 'hospital'      ? root.id : undefined,
    department_id:    root.type === 'department'    ? root.id : undefined,
  };

  return root.children.map(child => transformNode(child, seedIds));
}